import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { createUser, updateUser } from '@/modules/(identity)/services/userApi';
import Select, { ActionMeta, SingleValue } from 'react-select';
import { getPublishers } from '@/api/publisherApi';
import { getAgencies } from '@/api/agencyApi';
import { getAdvertisers } from '@/api/advertiserApi';
import { createOption, Option, telefoneMask } from '@/utils';
import { useProfile } from '@/hooks/UserHooks';
import { User } from '@/modules/(identity)/types/user';
import InputMask from 'react-input-mask';
import { getAdNetworks } from '@/api/adNetworkApi';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ModalProps {
    isOpen: boolean;
    toggle: () => void;
    user: User | null;
    onAddData: (newData: User) => void;
    onUpdateData: (newData: User) => void;
}

const userStatusOptions = [
    {
        options: [
            createOption('Ativo', '1'),
            createOption('Inativo', '0'),
        ],
    },
];

const AddEditUserModal = ({ user, isOpen, toggle, onAddData, onUpdateData }: ModalProps) => {
    const { userProfile } = useProfile();
    const [loading, setLoading] = useState<boolean>(false);
    const [publishers, setPublishers] = useState<any>([]);
    const [selectPublisherValue, setSelectPublisherValue] = useState<SingleValue<Option>>();
    const [advertisers, setAdvertisers] = useState<any>([]);
    const [selectAdvertiserValue, setSelectAdvertiserValue] = useState<SingleValue<Option>>();
    const [agencies, setAgencies] = useState<any>([]);
    const [adNetworks, setAdNetworks] = useState<any>([]);
    const [selectAgencyValue, setSelectAgencyValue] = useState<SingleValue<Option>>();
    const [selectAdNetworkValue, setSelectAdNetworkValue] = useState<SingleValue<Option>>();
    const [userStatusValue, setUserStatusValue] = useState<Option>();

    const toggleModal = () => {
        toggle();

        if (isOpen) {
            formik.resetForm();
            setSelectPublisherValue(undefined);
            setSelectAdvertiserValue(undefined);
            setSelectAgencyValue(undefined);
            setSelectAdNetworkValue(undefined);
            setUserStatusValue(undefined);
        }
    };

    useEffect(() => {
        if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
            getPublishers().then(setPublishers);
            getAgencies().then(setAgencies);
            getAdvertisers().then(setAdvertisers);
            getAdNetworks().then(setAdNetworks);
        }
    }, []);

    useEffect(() => {
        if (user) {
            setUserStatusValue(createOption(user.status ? 'Ativo' : 'Inativo', user.status));
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            if (publishers && user.publisher) {
                selectPublisherOnChange(createOption(user.publisher.name, user.publisher.id));
            }

            if (agencies && user.agency) {
                selectAgencyOnChange(createOption(user.agency.name, user.agency.id));
            }

            if (adNetworks && user.adNetwork) {
                selectAdNetworkOnChange(createOption(user.adNetwork.name, user.adNetwork.id));
            }

            if (advertisers && user.advertiser) {
                selectAdvertiserOnChange(createOption(user.advertiser.name, user.advertiser.id));
            }
        }
    }, [publishers, agencies, adNetworks, advertisers, user]);

    const selectPublisherOnChange = (newValue: SingleValue<Option>) => {
        setSelectPublisherValue(newValue);
        formik.setFieldValue('publisherId', newValue ? Number(newValue.value) : '');
    };

    const selectAgencyOnChange = (newValue: SingleValue<Option>) => {
        setSelectAgencyValue(newValue);
        formik.setFieldValue('agencyId', newValue ? Number(newValue.value) : '');
    };

    const selectAdNetworkOnChange = (newValue: SingleValue<Option>) => {
        setSelectAdNetworkValue(newValue);
        formik.setFieldValue('adNetworkId', newValue ? Number(newValue.value) : '');
    };

    const selectAdvertiserOnChange = (newValue: SingleValue<Option>) => {
        setSelectAdvertiserValue(newValue);
        formik.setFieldValue('advertiserId', newValue ? Number(newValue.value) : '');
    };

    const userStatusOnChange = (newValue: any) => {
        setUserStatusValue(newValue);
        formik.setFieldValue('status', newValue ? Number(newValue.value) : '');
    };

    const minimalUserAge = new Date(new Date().setFullYear(new Date().getFullYear() - 16));

    const parseDateString = (value: string): Date | '' => {
        if (!value || value.length !== 10) return '';
        const [day, month, year] = value.split('/');
        const date = new Date(`${year}-${month}-${day}`);
        return isNaN(date.getTime()) ? '' : date;
    };

    const formatDate = (value: string) => {
        const [year, month, day] = value.split('-');
        return `${day}/${month}/${year}`;
    };

    const validationSchema = Yup.object({
        publisherId: Yup.number()
            .nullable()
            .test('one-of-four', 'Preencha apenas um dos campos: Publisher, Advertiser ou Agência ou Ad Network', function (value) {
                const { advertiserId, agencyId, adNetworkId } = this.parent;
                const filledFields = [value, advertiserId, agencyId, adNetworkId].filter(Boolean).length;
                if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                    return true;
                }
                return filledFields === 1;
            }),
        advertiserId: Yup.number()
            .nullable()
            .test('one-of-four', 'Preencha apenas um dos campos: Publisher, Advertiser ou Agência ou Ad Network', function (value) {
                const { publisherId, agencyId, adNetworkId } = this.parent;
                const filledFields = [value, publisherId, agencyId, adNetworkId].filter(Boolean).length;
                if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                    return true;
                }
                return filledFields === 1;
            }),
        agencyId: Yup.number()
            .nullable()
            .test('one-of-four', 'Preencha apenas um dos campos: Publisher, Advertiser ou Agência ou Ad Network', function (value) {
                const { publisherId, advertiserId, adNetworkId } = this.parent;
                const filledFields = [value, publisherId, advertiserId, adNetworkId].filter(Boolean).length;
                if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                    return true;
                }
                return filledFields === 1;
            }),
        adNetworkId: Yup.number()
            .nullable()
            .test('one-of-four', 'Preencha apenas um dos campos: Publisher, Advertiser, Agência ou Ad Network', function (value) {
                const { publisherId, advertiserId, agencyId } = this.parent;
                const filledFields = [value, publisherId, advertiserId, agencyId].filter(Boolean).length;
                if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                    return true;
                }
                return filledFields === 1;
            }),
        name: Yup.string().trim().required('Nome é obrigatório'),
        email: Yup.string().lowercase().email('Email inválido').required('Email é obrigatório'),
        phone: Yup.string()
            .trim()
            .nullable()
            .transform((value) => value?.replace(/\D/g, ''))
            .min(10, 'Telefone inválido')
            .max(11, 'Telefone inválido'),
        birthday: Yup.string()
            .nullable()
            .test('valid-date', 'Data inválida. Use o formato DD/MM/YYYY', function (value) {
                if (!value) return true;
                const parsedDate = parseDateString(value);
                return parsedDate !== null;
            })
            .test('min-age', 'Usuário deve ter no mínimo 16 anos', function (value) {
                if (!value) return true;
                const parsedDate = parseDateString(value);
                if (parsedDate === null) return false;
                return parsedDate <= minimalUserAge;
            }),
        jobTitle: Yup.string().trim().nullable(),
        status: Yup.number()
            .transform((value) => Number(value))
            .required('Status é obrigatório'),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            id: user?.id || '',
            publisherId: user?.publisher?.id || '',
            advertiserId: user?.advertiser?.id || '',
            agencyId: user?.agency?.id || '',
            adNetworkId: user?.adNetwork?.id || '',
            name: user?.name || '',
            email: user?.email || '',
            phone: user?.phone || '',
            birthday: user?.birthday ? formatDate(user.birthday) : '',
            jobTitle: user?.jobTitle || '',
            status: user?.status || 1,
        },
        validationSchema,
        onSubmit: (values: any) => {
            setLoading(true);

            if (!values.publisherId) {
                values.publisherId = null;
            }

            if (!values.advertiserId) {
                values.advertiserId = null;
            }

            if (!values.agencyId) {
                values.agencyId = null;
            }

            if (!values.adNetworkId) {
                values.adNetworkId = null;
            }

            if (values.birthday) {
                const [day, month, year] = values.birthday.split('/');
                values.birthday = `${year}-${month}-${day}`;
            }

            if (user) {
                updateUser(values)
                    .then((res: any) => {
                        toggleModal();
                        toast.success('Usuário editado com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createUser(values)
                    .then((res: any) => {
                        toggleModal();
                        toast.success('Usuário cadastrado com sucesso!');
                        onAddData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    const getFieldError = (fieldName: string) => {
        const touched = formik.touched[fieldName as keyof typeof formik.touched];
        const error = formik.errors[fieldName as keyof typeof formik.errors];
        return touched && error ? error : null;
    };

    const isFieldInvalid = (fieldName: string) => {
        return !!getFieldError(fieldName);
    };

    return (
        <Dialog open={isOpen} onOpenChange={toggleModal}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>{user ? 'Editar' : 'Adicionar'} usuário</DialogTitle>
                    <DialogDescription>
                        {user ? 'Edite as informações do usuário.' : 'Preencha as informações para cadastrar um novo usuário.'}
                    </DialogDescription>
                </DialogHeader>
                <ScrollArea className="max-h-[60vh]">
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        formik.handleSubmit();
                        return false;
                    }}>
                        <div className="space-y-4 py-4">
                            {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="publisherId">Publisher</Label>
                                        <Select
                                            isClearable={true}
                                            isSearchable={true}
                                            name="publisherId"
                                            options={
                                                publishers &&
                                                publishers.map((publisher: any) => {
                                                    return createOption(publisher.name, String(publisher.id));
                                                })
                                            }
                                            styles={{
                                                control: (baseStyles) => ({
                                                    ...baseStyles,
                                                    borderColor: isFieldInvalid('publisherId') ? 'red' : '#ced4da',
                                                }),
                                            }}
                                            onChange={selectPublisherOnChange}
                                            value={selectPublisherValue}
                                        />
                                        {getFieldError('publisherId') && (
                                            <p className="text-sm text-destructive">{getFieldError('publisherId')}</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="advertiserId">Advertiser</Label>
                                        <Select
                                            isClearable={true}
                                            isSearchable={true}
                                            name="advertiserId"
                                            options={
                                                advertisers &&
                                                advertisers.map((advertiser: any) => {
                                                    return { value: advertiser.id, label: advertiser.name };
                                                })
                                            }
                                            styles={{
                                                control: (baseStyles) => ({
                                                    ...baseStyles,
                                                    borderColor: isFieldInvalid('advertiserId') ? 'red' : '#ced4da',
                                                }),
                                            }}
                                            onChange={selectAdvertiserOnChange}
                                            value={selectAdvertiserValue}
                                        />
                                        {getFieldError('advertiserId') && (
                                            <p className="text-sm text-destructive">{getFieldError('advertiserId')}</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="agencyId">Agência</Label>
                                        <Select
                                            isClearable={true}
                                            isSearchable={true}
                                            name="agencyId"
                                            options={
                                                agencies &&
                                                agencies.map((agency: any) => {
                                                    return { value: agency.id, label: agency.name };
                                                })
                                            }
                                            styles={{
                                                control: (baseStyles) => ({
                                                    ...baseStyles,
                                                    borderColor: isFieldInvalid('agencyId') ? 'red' : '#ced4da',
                                                }),
                                            }}
                                            onChange={selectAgencyOnChange}
                                            value={selectAgencyValue}
                                        />
                                        {getFieldError('agencyId') && (
                                            <p className="text-sm text-destructive">{getFieldError('agencyId')}</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="adNetworkId">Ad Network</Label>
                                        <Select
                                            isClearable={true}
                                            isSearchable={true}
                                            name="adNetworkId"
                                            options={
                                                adNetworks &&
                                                adNetworks.map((adNetwork: any) => {
                                                    return { value: adNetwork.id, label: adNetwork.name };
                                                })
                                            }
                                            styles={{
                                                control: (baseStyles) => ({
                                                    ...baseStyles,
                                                    borderColor: isFieldInvalid('adNetworkId') ? 'red' : '#ced4da',
                                                }),
                                            }}
                                            onChange={selectAdNetworkOnChange}
                                            value={selectAdNetworkValue}
                                        />
                                        {getFieldError('adNetworkId') && (
                                            <p className="text-sm text-destructive">{getFieldError('adNetworkId')}</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="name">Nome</Label>
                                <Input
                                    id="name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className={isFieldInvalid('name') ? 'border-destructive' : ''}
                                />
                                {getFieldError('name') && (
                                    <p className="text-sm text-destructive">{getFieldError('name')}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className={isFieldInvalid('email') ? 'border-destructive' : ''}
                                />
                                {getFieldError('email') && (
                                    <p className="text-sm text-destructive">{getFieldError('email')}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="phone">Telefone</Label>
                                <Input
                                    id="phone"
                                    value={telefoneMask(formik.values.phone)}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className={isFieldInvalid('phone') ? 'border-destructive' : ''}
                                />
                                {getFieldError('phone') && (
                                    <p className="text-sm text-destructive">{getFieldError('phone')}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="birthday">Aniversário</Label>
                                <InputMask
                                    id="birthday"
                                    mask="99/99/9999"
                                    value={formik.values.birthday}
                                    onChange={formik.handleChange}
                                >
                                    {(inputProps: any) => (
                                        <Input
                                            {...inputProps}
                                            className={isFieldInvalid('birthday') ? 'border-destructive' : ''}
                                        />
                                    )}
                                </InputMask>
                                {getFieldError('birthday') && (
                                    <p className="text-sm text-destructive">{getFieldError('birthday')}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="jobTitle">Cargo</Label>
                                <Input
                                    id="jobTitle"
                                    value={formik.values.jobTitle}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className={isFieldInvalid('jobTitle') ? 'border-destructive' : ''}
                                />
                                {getFieldError('jobTitle') && (
                                    <p className="text-sm text-destructive">{getFieldError('jobTitle')}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="user_status">Status</Label>
                                <Select
                                    id="user_status"
                                    name="user_status"
                                    styles={{
                                        control: (baseStyles) => ({
                                            ...baseStyles,
                                            borderColor: isFieldInvalid('status') ? 'red' : '#ced4da',
                                        }),
                                    }}
                                    defaultValue={userStatusOptions[0].options[0]}
                                    onChange={userStatusOnChange}
                                    value={userStatusValue}
                                    options={userStatusOptions}
                                />
                                {getFieldError('status') && (
                                    <p className="text-sm text-destructive">{getFieldError('status')}</p>
                                )}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={toggleModal}>
                                Cancelar
                            </Button>
                            <Button type="submit" disabled={loading}>
                                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {user ? 'Atualizar' : 'Cadastrar'}
                            </Button>
                        </DialogFooter>
                    </form>
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
};

export default AddEditUserModal;
